import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  authenticate,
  getSyncPairs,
  getToken,
  updateSyncPair,
} from '~/api/evisync';
import { getStorageIntegrations } from '~/common/constants/integrations';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Text } from '~/eds/src/components/Text/Text';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { StorageProviderType } from '~/enums/StorageProviderType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import { removeQueryParam } from '~/utils/browser';
import { createSyncPairData } from '~/utils/integrations';

import { getSyncPairCardStatus, resetSyncPairAuthCode } from '../util';
import IntegrationCardList from './IntegrationCardList';

function StorageIntegrationCardList({ isCentered = false }) {
  const { location, navigate } = useRouting();
  const hideAwsS3SyncPairOption = useFlag(FlagType.HideAwsS3SyncPairOption);
  const { params } = useRouting();

  const storageSyncPairs = useSelector(
    slices.integrations.selectors.selectSyncPairs,
  );

  const storageIntegrations = getStorageIntegrations();
  const syncPairs = Object.values(storageSyncPairs).map((syncPair) => ({
    ...storageIntegrations[syncPair.provider],
    ...syncPair,
  }));
  const intl = useIntl();
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const hasTokenId = Boolean(activeSyncPair?.tokenId);
  const isPairCreated = Boolean(activeSyncPair?.id);
  const isLoadingToken = Boolean(activeSyncPair?.state);

  const { clientId } = params;

  useAsync(
    getSyncPairs,
    { clientId },
    {
      condition: true,
      errorToastMessage: intl.formatMessage({
        id: 'StorageIntegrationCardList.unableToFetchSyncPairsError',
        defaultMessage: 'Unable to fetch sync pairs, please try again.',
        description: 'Error message when fetching storage sync pairs',
      }),
      successHandler: (data) =>
        dispatch(slices.integrations.actions.setSyncPairs(data)),
    },
  );

  const authenticateErrorMessage = intl.formatMessage(
    {
      id: 'Authentication.errorMessage',
      defaultMessage: 'Unable to contact {name}, please try again.',
      description:
        'Error message when authentication fails. Example: name = "Google Drive"',
    },
    {
      name: activeSyncPair?.name,
    },
  );

  useEffect(() => {
    if (!hasTokenId && activeSyncPair?.state && activeSyncPair?.code) {
      dispatch(
        slices.integrations.actions.setActiveSyncPair(
          resetSyncPairAuthCode(activeSyncPair),
        ),
      );
      getToken({
        code: activeSyncPair?.code,
        provider: activeSyncPair?.provider,
        state: activeSyncPair?.state,
      })
        .then((response) => {
          const updatedActiveSyncPair = {
            ...activeSyncPair,
            tokenId: response.id,
          };
          dispatch(
            slices.integrations.actions.setActiveSyncPair(
              resetSyncPairAuthCode(updatedActiveSyncPair),
            ),
          );

          if (isPairCreated) {
            executeUpdate();
            handleUpdateNext();
          } else {
            handleCreateNext();
          }

          removeQueryParam(location, navigate, QueryParamType.States);
        })
        .catch(() => {
          dispatch(
            slices.integrations.actions.setManageSyncPairStage(
              ManageSyncPairStageType.Provider,
            ),
          );
          showToast(ERROR, authenticateErrorMessage);
        });
    }
  }, [activeSyncPair]); // eslint-disable-line react-hooks/exhaustive-deps

  const { executor: executeAuthenticate } = useAsync(
    authenticate,
    { provider: activeSyncPair?.provider, returnTo: window.location.href },
    {
      errorToastMessage: authenticateErrorMessage,
      successHandler: (response) => {
        window.location.href = response.url;
      },
    },
  );

  const { executor: executeUpdate } = useAsync(updateSyncPair, activeSyncPair, {
    errorToastMessage: intl.formatMessage({
      id: 'Authentication.updateErrorMessage',
      defaultMessage: 'Unable to update sync pair, please try again.',
      description: 'Error message when updating sync pair fails',
    }),
    successToastMessage: intl.formatMessage(
      {
        id: 'Authentication.updateSuccessMessage',
        defaultMessage: 'Sync pair updated successfully name: {name}',
        description:
          'Success message when sync pair is updated. Example: name = "Google Drive"',
      },
      { name: activeSyncPair?.name },
    ),
    successHandler: (response) => {
      dispatch(slices.integrations.actions.setActiveSyncPair(response));
    },
  });

  const handleCreateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Folders,
      ),
    );
  };

  const handleUpdateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.None,
      ),
    );
  };

  const renderSyncPairCardStatus = ({ color, icon, count }) => {
    if (!count || count <= 0) {
      return <Icon color={color} icon={icon} />;
    }

    return (
      <FlexLayout alignItems="center" space={2}>
        <Icon color={color} icon={icon} />
        <Text color={color} variant="tiny-bold">
          {count}
        </Text>
      </FlexLayout>
    );
  };

  let integrationCards = Object.values(storageIntegrations)
    .filter((integration) => {
      if (hideAwsS3SyncPairOption) {
        return integration.provider !== StorageProviderType.AmazonS3;
      }

      return integration;
    })
    .map((integration) => ({
      ...integration,
      status: renderSyncPairCardStatus(
        getSyncPairCardStatus(integration.provider, syncPairs),
      ),
      description: intl.formatMessage(
        {
          id: `StorageIntegrationCardList.providerDescription`,
          defaultMessage:
            'Synchronize documents stored on {integration} with Evisort.',
          description:
            'Description for storage integration provider. Example: integration = "Salesforce"',
        },
        {
          integration: integration.name,
        },
      ),
      action: {
        id: OnboardingIdType.AddSyncPairLink,
        text: intl.formatMessage({
          id: 'StorageIntegrationCardList.addSyncPair',
          defaultMessage: 'Add Sync Pair',
          description:
            'Button text to add a sync pair for storage integration | maxLength=60',
        }),
        onClick: () => {
          const syncPairData = createSyncPairData(integration);
          dispatch(slices.integrations.actions.setActiveSyncPair(syncPairData));
          executeAuthenticate();
        },
      },
    }));

  if (isLoadingToken) {
    return (
      <FlexLayout alignItems="center" flexDirection="column" space={12}>
        <ProviderLogo provider={activeSyncPair?.provider} />
        <LoadingSpinner />
      </FlexLayout>
    );
  }

  return (
    <IntegrationCardList
      isCentered={isCentered}
      integrationCards={integrationCards}
    />
  );
}

export default StorageIntegrationCardList;
